import React from 'react';
import { Link } from 'react-router-dom';
import { VIGIL_PRODUCT } from './websiteData';
import { VigilScanIllustration } from './WebsiteIllustrations';

const LANGS = ['Go', 'Java', 'Python', 'JavaScript', 'TypeScript', 'SAP ABAP', 'COBOL'];

const COMPARE_ROWS = [
  { feature: 'OWASP Top 10 coverage', vigil: 'yes', semgrep: 'partial', codeql: 'partial' },
  { feature: 'Cold start time', vigil: '38ms', semgrep: '2–8s', codeql: '10–30s' },
  { feature: 'PRL custom rules', vigil: 'yes', semgrep: 'partial', codeql: 'partial' },
  { feature: 'SBOM correlation', vigil: 'yes', semgrep: 'no', codeql: 'no' },
  { feature: 'SARIF output', vigil: 'yes', semgrep: 'yes', codeql: 'yes' },
  { feature: 'Zero external runtime', vigil: 'yes', semgrep: 'no', codeql: 'no' },
  { feature: 'Embeddable as library', vigil: 'yes', semgrep: 'no', codeql: 'no' },
  { feature: 'EU CRA evidence', vigil: 'yes', semgrep: 'no', codeql: 'no' },
];

const PRL_EXAMPLE = `rule xss_reflected {
  meta:
    id       = "XSS-REFL-001"
    severity = "high"
    cwe      = [79]

  source {
    kind = CallExpression
    name = /req\\.(query|params|body)/
  }

  sink {
    kind    = CallExpression
    name    = /res\\.(send|render|write)/
    tainted = true
  }

  sanitizer {
    kind = CallExpression
    name = /he\\.encode|DOMPurify\\.sanitize/
  }
}`;

export default function WebsiteVigilPage() {
  const { name, icon, accentColor, accentRgb, tagline, heroTitle, heroLead, features, useCases } = VIGIL_PRODUCT;

  return (
    <div className="dgw-page-wrap">
      {/* Hero */}
      <section style={{
        padding: '80px clamp(20px,5vw,60px) 60px',
        background: '#04080f',
        borderBottom: '1px solid rgba(255,255,255,.08)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Glow */}
        <div style={{
          position:'absolute', top:'-100px', right:'-100px',
          width:500, height:500, borderRadius:'50%',
          background:`radial-gradient(circle, rgba(${accentRgb},.15), transparent 70%)`,
          pointerEvents:'none',
        }} />
        <div style={{maxWidth:1280, margin:'0 auto', position:'relative', zIndex:1}}>
          <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:24}}>
            <span style={{fontSize:32}}>{icon}</span>
            <span style={{
              fontSize:12, fontWeight:700, letterSpacing:'.8px', textTransform:'uppercase',
              color: accentColor,
              background:`rgba(${accentRgb},.1)`,
              border:`1px solid rgba(${accentRgb},.25)`,
              padding:'5px 14px', borderRadius:100,
            }}>{tagline}</span>
          </div>
          <h1 style={{fontSize:'clamp(36px,5vw,64px)', fontWeight:800, color:'#fff', letterSpacing:'-1.5px', lineHeight:1.08, marginBottom:20}}>
            {heroTitle}
          </h1>
          <p style={{fontSize:18, color:'#94a3b8', lineHeight:1.7, maxWidth:600, marginBottom:32}}>{heroLead}</p>
          <div style={{display:'flex', gap:12, flexWrap:'wrap'}}>
            <Link to="/trial?plan=professional&product=vigil" className="dgw-btn dgw-btn-primary">Start free →</Link>
            <Link to="/contact?type=demo" className="dgw-btn dgw-btn-ghost">Book demo</Link>
            <Link to="/website/product/prism" className="dgw-btn dgw-btn-prism">See the PRISM engine →</Link>
          </div>
        </div>
      </section>

      <div style={{maxWidth:1280, margin:'0 auto', padding:'80px clamp(20px,5vw,60px)'}}>

        {/* Language badges */}
        <div style={{marginBottom:80}}>
          <div className="dgw-section-header" style={{marginBottom:32}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Language support</span>
            <h2 style={{color:'var(--dgw-text)'}}>7 languages. 140+ security rules.</h2>
            <p className="dgw-section-lead">Go, Java, Python, JavaScript, TypeScript, SAP ABAP, and COBOL. One PRL rule applies across all of them — write it once, deploy to every scan.</p>
          </div>
          <div style={{display:'flex', flexWrap:'wrap', gap:10, justifyContent:'center'}}>
            {LANGS.map(lang => (
              <span key={lang} style={{
                padding:'8px 18px', borderRadius:100,
                background:`rgba(${accentRgb},.08)`,
                border:`1px solid rgba(${accentRgb},.2)`,
                color:'#c4b5fd', fontSize:14, fontWeight:600,
              }}>{lang}</span>
            ))}
          </div>
        </div>

        {/* Scan illustration */}
        <div className="dgw-2col" style={{marginBottom:80, gap:48}}>
          <div>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`, display:'inline-block', marginBottom:16}}>See it in action</span>
            <h2 style={{color:'var(--dgw-text)', fontSize:'clamp(22px,3vw,34px)', fontWeight:800, marginBottom:16}}>243ms. Source to finding.</h2>
            <p style={{color:'var(--dgw-muted)', fontSize:15, lineHeight:1.7, marginBottom:20}}>
              Vigil runs on the PRISM CPG. The graph is already built — no separate parse step.
              140+ PRL rules execute concurrently. Every finding includes the CWE,
              the exact taint path from source to sink, and a remediation suggestion.
            </p>
            <ul style={{listStyle:'none', padding:0, margin:0, display:'flex', flexDirection:'column', gap:10}}>
              {['CRITICAL findings bubble to the top automatically','Flow paths show source → propagation → sink','Policy gate fails the CI build on your threshold'].map(t => (
                <li key={t} style={{display:'flex', gap:10, fontSize:14, color:'var(--dgw-muted)'}}>
                  <span style={{color: accentColor, fontWeight:700, flexShrink:0}}>→</span>{t}
                </li>
              ))}
            </ul>
          </div>
          <div style={{borderRadius:14, overflow:'hidden', boxShadow:'0 8px 40px rgba(0,0,0,0.15)', border:'1px solid rgba(139,92,246,.2)'}}>
            <VigilScanIllustration />
          </div>
        </div>

        {/* Features grid */}
        <div style={{marginBottom:80}}>
          <div className="dgw-section-header" style={{marginBottom:40}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Capabilities</span>
            <h2 style={{color:'var(--dgw-text)'}}>What you get out of the box</h2>
          </div>
          <div className="dgw-feature-grid">
            {features.map(f => (
              <div key={f.title} className="dgw-feature-item" style={{'--accent': accentColor}}>
                <div className="dgw-feature-item-icon">{f.icon}</div>
                <div className="dgw-feature-item-title">{f.title}</div>
                <div className="dgw-feature-item-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* PRL example */}
        <div style={{marginBottom:80}}>
          <div className="dgw-section-header" style={{marginBottom:40, textAlign:'left'}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>PRL Rule Language</span>
            <h2 style={{color:'var(--dgw-text)', textAlign:'left'}}>Express rules, not regular expressions.</h2>
            <p className="dgw-section-lead" style={{textAlign:'left', margin:0}}>
              PRL is a human-readable DSL for expressing vulnerability patterns as source–sink–sanitizer triples.
              Vigil compiles PRL rules to CPG queries automatically.
            </p>
          </div>
          <div className="dgw-code-block">
            <div className="dgw-code-block-header">
              <span>xss_reflected.prl</span>
              <span style={{color:'#8b5cf6'}}>PRISM Rule Language</span>
            </div>
            <pre>{PRL_EXAMPLE}</pre>
          </div>
        </div>

        {/* Comparison */}
        <div style={{marginBottom:80}}>
          <div className="dgw-section-header" style={{marginBottom:40}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Comparison</span>
            <h2 style={{color:'var(--dgw-text)'}}>Vigil vs the alternatives</h2>
          </div>
          <div className="dgw-spec-table-wrap">
            <table className="dgw-compare-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th style={{color:'#c4b5fd'}}>👁️ Vigil</th>
                  <th>Semgrep</th>
                  <th>CodeQL</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map(row => (
                  <tr key={row.feature}>
                    <td><strong style={{color:'#e2e8f0'}}>{row.feature}</strong></td>
                    <td className={row.vigil === 'yes' ? 'c-yes' : row.vigil === 'no' ? 'c-no' : ''}>{row.vigil === 'yes' ? '✓ Yes' : row.vigil === 'no' ? '✗ No' : row.vigil}</td>
                    <td className={row.semgrep === 'yes' ? 'c-yes' : row.semgrep === 'no' ? 'c-no' : 'c-partial'}>{row.semgrep === 'yes' ? '✓ Yes' : row.semgrep === 'no' ? '✗ No' : '~ ' + row.semgrep}</td>
                    <td className={row.codeql === 'yes' ? 'c-yes' : row.codeql === 'no' ? 'c-no' : 'c-partial'}>{row.codeql === 'yes' ? '✓ Yes' : row.codeql === 'no' ? '✗ No' : '~ ' + row.codeql}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Use cases */}
        <div style={{marginBottom:60}}>
          <div className="dgw-section-header" style={{marginBottom:40}}>
            <span className="dgw-section-tag" style={{color: accentColor, background:`rgba(${accentRgb},.1)`, borderColor:`rgba(${accentRgb},.25)`}}>Use cases</span>
            <h2 style={{color:'var(--dgw-text)'}}>The teams running Vigil</h2>
          </div>
          <div className="dgw-grid-2">
            {useCases.map(u => (
              <div key={u.title} className="dgw-card">
                <h4 style={{color:'var(--dgw-text)', marginBottom:8, fontSize:15}}>{u.title}</h4>
                <p style={{color:'var(--dgw-muted)', fontSize:14, lineHeight:1.6, margin:0}}>{u.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div style={{
          padding:48, borderRadius:20,
          background:`linear-gradient(135deg, rgba(${accentRgb},.12), rgba(${accentRgb},.06))`,
          border:`1px solid rgba(${accentRgb},.25)`,
          textAlign:'center',
        }}>
          <h3 style={{fontSize:28, fontWeight:800, color:'var(--dgw-text)', marginBottom:12}}>Your pipeline. Our rules. Ship faster.</h3>
          <p style={{color:'var(--dgw-muted)', marginBottom:28, fontSize:16}}>Free trial. No credit card. 243ms from code to finding.</p>
          <div style={{display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap'}}>
            <Link to="/trial?plan=professional&product=vigil" className="dgw-btn dgw-btn-primary dgw-btn-lg">Start free trial →</Link>
            <Link to="/contact?type=demo" className="dgw-btn dgw-btn-ghost dgw-btn-lg">Book a demo</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
